import Image from "next/image"
import { Anchor } from "../../app/components/anchor"
import PawHero from "../../assets/images/paw-hero.png"
import DogVetCare from "../../assets/images/dog-vet-care.png"
import Shadow from "../../assets/images/blue-shadow.png"
import ShadowFooter from "../../assets/images/blue-shadow-footer.png"
import { PawIcon } from "../../components/svgs"
import breedsData from "../../constants/pet-breed/all-breeds.json"
import { urlifyName } from "../../constants/stateCityDictionary"
import { safelyRenderHtml } from "@/util/safelyRenderHtml"
export type DogBreed = {
  animal: string
  title: string
  breed: string
  originsAndTemperament: string
  sizeAndPhysicalNeeds: string
  commonHealthConsiderations: string
  preTransportationChecklist: string
  healthAndVaccinationRecords: string
  identificationAndTracking: string
  comfortItemsAndFamiliarScents: string
  tailoredTravelTips: string
  managingAnxietyAndStress: string
  exerciseAndBathroomBreaks: string
  temperatureControlAndHydration: string
  post_TransportationCare: string
  adjustmentToNewSurroundings: string
  monitoringHealthPostTravel: string
  continuingCareAndRoutine: string
}

export const DogBreeds = ({
  breeds,
  supplies,
  heroImageData,
}: {
  breeds: DogBreed
  supplies: string[]
  heroImageData?: any
}) => {
  return (
    <>
      <section
        id="article"
        className="mx-auto pt-10 mt-10 px-4 md:px-1 max-w-[1145px]"
      >
        <h2 className="font-montserrat text-blue7 font-medium text-3xl md:text-4xl leading-11 md:text-center mb-9">
          {breeds.title}
        </h2>
        <article className="w-full max-auto">
          <div className="prose-h4:mb-6 prose-h4:pt-4 prose-h4:font-semibold prose-h4:text-lg">
            {safelyRenderHtml(breeds.originsAndTemperament)}
          </div>
          <div className="prose-h4:mb-6 prose-h4:pt-4 prose-h4:font-semibold prose-h4:text-lg">
            {safelyRenderHtml(breeds.sizeAndPhysicalNeeds)}
          </div>

          <div className="prose-h4:mb-6 prose-h4:pt-4 prose-h4:font-semibold prose-h4:text-lg">
            {safelyRenderHtml(breeds.commonHealthConsiderations)}
          </div>
        </article>
      </section>
      <section
        id="prepare-for-trip"
        className="mx-auto pt-10 mt-10 max-w-[1160px]"
      >
        <div className="bg-white p-4 md:p-8 max-w-7xl mx-auto w-full mb-14">
          <h2 className="font-montserrat text-blue7 font-medium text-3xl md:text-4xl leading-11 md:text-center mb-4">
            How to prepare your {breeds.breed} for transport
          </h2>
          <p className="max-w-3xl mx-auto mb-10 pb-12 md:text-center">
            At CitizenShipper, we pride ourselves on making pet transportation a
            breeze. However, there are things you can do ahead of time to make
            your best friend's trip safer and more comfortable.
          </p>
          <div className="flex flex-col md:flex-row justify-center mx-auto gap-20">
            <div className="md:w-2/5">
              <Image
                src={DogVetCare.src}
                alt={`Man with ${breeds?.animal}`}
                className="rounded-lg object-cover w-full md:w-[396px] md:h-[510px] "
                width={396}
                height={510}
                loading="lazy"
              />
            </div>
            <div className="mt-10 md:mt-0 md:w-3/5 md:pr-10 lg:pr-20">
              <h3 className="mb-4 pt-4 text-2.5xl font-semibold ">
                Talk to your veterinarian
              </h3>
              <p className="mb-4">
                Your pet's safety is number one – that's why checking in with
                your vet before finalizing any travel plans is essential.
              </p>
              <p>
                Can't make it to the vet's office in time? Conveniently video
                chat with a qualified veterinarian at Vetster – one of our
                amazing partners.
              </p>
              <h3 className="text-xl mb-4 mt-6 max-w-xl">
                Here are the questions you'll want to ask your vet:
              </h3>
              <ul className="px-0">
                <li className="border border-black-200 px-5 py-3.5 mb-2.5 rounded">
                  Is my pet healthy enough for transport?
                </li>
                <li className="border border-black-200 px-5 py-3.5 mb-2.5 rounded">
                  Is my pet up to date on their vaccines?
                </li>
                <li className="border border-black-200 px-5 py-3.5 rounded">
                  Is my pet's microchip still in place and scannable?
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
      <section
        id="vaccination-records"
        className="mx-auto md:pt-10 md:mt-10 max-w-[1160px]"
      >
        <div className="bg-white p-4 md:p-8 max-w-7xl mx-auto w-full mb-14">
          <div className="flex flex-col md:flex-row justify-center mx-auto">
            <div className="mt-10 md:mt-0 md:w-1/2 md:pr-10 lg:pr-20">
              {
                <div className="prose-h4:text-xl  prose-h4:mb-3 prose-h4:md:leading-[54px] prose-h4:-mt-2">
                  {safelyRenderHtml(breeds.healthAndVaccinationRecords)}
                </div>
              }
              <div className="prose-h4:text-xl  prose-h4:mb-3 prose-h4:md:leading-[54px] prose-h4:-mt-2">
                {safelyRenderHtml(breeds.identificationAndTracking)}
              </div>
            </div>
            <div className="md:w-1/2 px-2 mb-5 md:mb-0">
              <div className="relative">
                <Image
                  className="relative z-20 w-[347px] h-[300px] object-cover object-top rounded-2xl rounded-br-full rounded-bl-full rounded-tl-full -right-2.5 top-4 md:-right-2 md:top-3"
                  src={heroImageData?.portrait_image_url || ""}
                  alt={heroImageData?.portrait_image_alt_text || "Dog"}
                  height={300}
                  width={347}
                />
                <Image
                  src={Shadow.src}
                  alt="pets shipping"
                  className="absolute top-1 z-0 object-cover w-[343px] h-[300px]"
                  height={300}
                  width={343}
                  loading="lazy"
                />
                <Image
                  src={ShadowFooter.src}
                  alt="dog shipping"
                  className="object-contain w-[108px] h-[90px] absolute -bottom-4 -right-4 md:right-0 md:left-[245px] flex items-end justify-end z-50"
                  width={108}
                  height={90}
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
      <section id="supplies" className="mx-auto pt-10 mt-10 max-w-[1160px]">
        <div className="bg-white p-4 md:p-8 max-w-7xl mx-auto w-full mb-14">
          <div className="flex flex-col md:flex-row justify-center mx-auto">
            <div className="md:w-1/2">
              <Image
                src={PawHero.src}
                alt="Man with dog"
                className="object-cover w-full md:w-[403px] md:h-[536px] "
                width={403}
                height={536}
                loading="lazy"
              />
            </div>
            <div className="mt-10 md:mt-0 md:w-1/2 md:pl-10 lg:pl-4">
              <div className="prose-h4:text-2xl  prose-h4:xl:text-[28px] prose-h4:mb-3 prose-h4:md:leading-[54px] prose-h4:-mt-2">
                {safelyRenderHtml(breeds.comfortItemsAndFamiliarScents)}
              </div>

              <p className="text-xl mb-4 max-w-xl font-semibold">
                Essential pet transport travel supplies include:
              </p>
              <ul className="grid md:grid-cols-2 justify-center md:justify-between gap-4 px-0">
                {supplies.map((feature) => (
                  <li key={feature} className="mb-2 md:mb-0 flex items-start">
                    <PawIcon className="h-6 w-6 stroke-blue7 fill-blue7 mr-2" />
                    <span className="no-whitespace no-wrap">{feature}</span>
                  </li>
                ))}
              </ul>
              <p>
                Traveling to a new place is exciting but stressful for your pet.
                Pack their favorite toys, a bed or a comfy blanket to help them
                enjoy their journey. Familiar items remind them of home and will
                soothe their anxiety.
              </p>
            </div>
          </div>
        </div>
      </section>
      <section
        id="article"
        className="mx-auto pt-10 mt-10 px-4 md:px-1 max-w-[1145px]"
      >
        <div className="font-montserrat font-medium text-2xl leading-11 md:text-center mb-6">
          {safelyRenderHtml(breeds.tailoredTravelTips)}
        </div>

        <article className="w-full max-auto">
          <div className="prose-h4:mb-6 prose-h4:pt-4 prose-h4:font-semibold prose-h4:text-lg">
            {safelyRenderHtml(breeds.managingAnxietyAndStress)}
          </div>
          <div className="prose-h4:mb-6 prose-h4:pt-4 prose-h4:font-semibold prose-h4:text-lg">
            {safelyRenderHtml(breeds.exerciseAndBathroomBreaks)}
          </div>

          <div className="prose-h4:mb-6 prose-h4:pt-4 prose-h4:font-semibold prose-h4:text-lg">
            {safelyRenderHtml(breeds.temperatureControlAndHydration)}
          </div>
        </article>
      </section>

      <section
        id="article"
        className="mx-auto pt-10 mt-10 mb-20 px-4 md:px-1 max-w-[1145px]"
      >
        <div className="text-center mx-auto w-full flex justify-center mb-10 pb-4">
          <div className="shadow-lg bg-gradient-to-br bg-blue7 bg-opacity-25 rounded-2xl p-2.5">
            <Image
              className="w-[447px] h-[280px] object-cover object-top rounded-2xl"
              src={heroImageData?.horizontal_image_url || ""}
              alt={heroImageData?.horizontal_image_alt_text || "Dog"}
              height={280}
              width={447}
            />
          </div>
        </div>
        <h2 className="font-montserrat font-medium text-2xl leading-11 md:text-center mb-6">
          Post Transportation Care for your {breeds.breed}
        </h2>
        <article className="w-full max-auto">
          <div className="prose-h4:mb-6 prose-h4:pt-4 prose-h4:font-semibold prose-h4:text-lg">
            {safelyRenderHtml(breeds.adjustmentToNewSurroundings)}
          </div>
          <div className="prose-h4:mb-6 prose-h4:pt-4 prose-h4:font-semibold prose-h4:text-lg">
            {safelyRenderHtml(breeds.monitoringHealthPostTravel)}
          </div>

          <div className="prose-h4:mb-6 prose-h4:pt-4 prose-h4:font-semibold prose-h4:text-lg">
            {safelyRenderHtml(breeds.continuingCareAndRoutine)}
          </div>
        </article>
      </section>
      <section
        id="ready-to-ship"
        className="p-4 md:text-center max-w-[1145px] mx-auto"
      >
        <h5 className="text-3xl md:text-4xl font-medium font-montserrat">
          Ready to ship your {breeds.breed}
        </h5>
        <p className="mt-4 mb-12 text-lg max-w-xl md:text-center w-full mx-auto">
          No hidden costs or upcharges. Just a simple and stress-free
          experience. Get started in two minutes or less!
        </p>
        <Anchor
          href={"/shipments/new/animals/dogs"}
          className="w-full md:w-auto px-16 rounded-md py-5 text-center bg-blue7 hover:bg-blue-700 mt-5 text-white hover:text-white text-md hover:no-underline"
        >
          Get Free Estimates
        </Anchor>
      </section>
      <section
        id="pet-breeds"
        className="p-4 md:p-0 max-w-[1145px] mx-auto md:text-center md:mt-10 pt-10 pb-10 mb-10 md:pb-20"
      >
        <p className="text-xl mb-10">
          Popular {breeds?.animal} breeds that we ship
        </p>
        <ul className="grid md:grid-cols-4 gap-x-10 gap-y-6 px-0 list-none">
          {breedsData.breeds.dogs
            .filter((item) => item != breeds.breed)
            .map((dog) => (
              <li key={dog} className="text-left">
                <Anchor href={`/dog-shipping/${urlifyName(dog)}`}>{dog}</Anchor>
              </li>
            ))}
        </ul>
      </section>
    </>
  )
}
